import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky, faLayerGroup, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import AppContext from '../context/AppContext';
import CommentsAPI from '../api/CommentsAPI';
import { CommentModel } from '../types/CommentModel';
import { RepairQueueItem } from '../types/CorrectionModel';
import { MoveType } from '../types/MoveType';
import { getIcon, moveTypeClass } from '../utils/ChessTypes';
import { patternLabel } from './patternThemes';

// One ply of the run-up: SAN, side, and the index of the position it produces in the board's line.
type Ply = { san: string; number: number; white: boolean; index: number };

// The queue carries the run-up as FENs only, so read the moves back off the positions: the legal move
// whose result matches the next FEN. Compared on piece placement alone, since clocks may differ.
function pliesFromFens(fens: string[]): Ply[] {
  const plies: Ply[] = [];
  const placement = (fen: string) => fen.split(' ')[0];
  for (let i = 0; i < fens.length - 1; i++) {
    let found: Ply | null = null;
    try {
      const c = new Chess(fens[i]);
      const number = c.moveNumber();
      const white = c.turn() === 'w';
      for (const candidate of c.moves({ verbose: true })) {
        const probe = new Chess(fens[i]);
        const mv = probe.move(candidate.san);
        if (mv != null && placement(probe.fen()) === placement(fens[i + 1])) {
          found = { san: mv.san, number, white, index: i + 1 };
          break;
        }
      }
    } catch {
      found = null;
    }
    // A gap would misalign every later ply, so stop.
    if (found == null) break;
    plies.push(found);
  }
  return plies;
}

// "in 6 days" / "3 weeks ago": the repetition ladder as a distance rather than a date.
function relativeDays(iso: string | null): string | null {
  if (iso == null) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const days = Math.round((then - Date.now()) / 86400000);
  const abs = Math.abs(days);
  const unit =
    abs === 0 ? 'today' : abs === 1 ? '1 day' : abs < 30 ? `${abs} days` : `${Math.round(abs / 30)} months`;
  if (abs === 0) return 'today';
  return days > 0 ? `in ${unit}` : `${unit} ago`;
}

// The context around the drill: what keeps happening, the player's own note, how the position was
// reached, and where it sits on the repetition ladder. No public discussion: notes are private (see
// CommentsController).
export default function RepairGamePanel(props: {
  item: RepairQueueItem;
  // Where the board currently sits within its line, and how to move it.
  cursor: number;
  onGoTo: (index: number) => void;
  // The mistake is named only once played; before that it would answer the question.
  revealed: boolean;
}): React.ReactNode {
  const { item, cursor, onGoTo, revealed } = props;
  const { isAuthenticated } = useContext(AppContext);
  const plies = useMemo(() => pliesFromFens(item.History), [item.History]);
  const listRef = useRef<HTMLDivElement>(null);

  // The mistake sits one ply past the run-up; the board puts the played move at that index.
  const mistakeIndex = item.History.length;
  const solveIndex = item.History.length - 1;

  // Keep the board's move in view (the run-up can be long). Scroll only this list's own scrollbar:
  // scrollIntoView would also scroll the page and jump the viewport when the verdict above pushes this
  // list down.
  useEffect(() => {
    const container = listRef.current;
    const current = container?.querySelector<HTMLElement>('.is-current');
    if (container == null || current == null) return;
    const containerRect = container.getBoundingClientRect();
    const currentRect = current.getBoundingClientRect();
    if (currentRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - currentRect.top;
    } else if (currentRect.bottom > containerRect.bottom) {
      container.scrollTop += currentRect.bottom - containerRect.bottom;
    }
  }, [cursor, plies.length]);

  const MistakeIcon = getIcon(item.MoveType);
  const themeName = patternLabel(item.CommentaryTheme ?? null);
  // One occurrence isn't a pattern.
  const recurring = themeName != null && item.ThemeOccurrences >= 2;

  return (
    <div className="cm-repair-panel">
      {/* This isn't one bad move, it's a thing you keep doing. Held back until the answer is in. */}
      {recurring && revealed ? (
        <section className="cm-repair-panel-section cm-repair-recurring">
          <h6 className="cm-repair-panel-head">
            <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
            This keeps happening
          </h6>
          <p className="cm-repair-recurring-line">
            <strong>{themeName}</strong> came up {item.ThemeOccurrences} times
            {item.ThemeGameCount > 1 ? ` across ${item.ThemeGameCount} of these games` : ''}.
          </p>
          {item.CommentaryTheme ? (
            <a
              className="cm-repair-recurring-link"
              href={`/patterns/${item.CommentaryTheme}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              See this pattern across all games &rarr;
            </a>
          ) : null}
        </section>
      ) : null}

      <section className="cm-repair-panel-section">
        <h6 className="cm-repair-panel-head">Moves</h6>
        <div className="cm-repair-moves" ref={listRef}>
          {plies.length === 0 ? (
            <p className="cm-repair-panel-empty">The run-up to this position isn’t available.</p>
          ) : (
            plies.map((ply) => (
              <React.Fragment key={ply.index}>
                {ply.white ? <span className="cm-repair-move-num">{ply.number}.</span> : null}
                <button
                  type="button"
                  className={`cm-repair-move${cursor === ply.index ? ' is-current' : ''}`}
                  onClick={() => onGoTo(ply.index)}
                >
                  {ply.san}
                </button>
              </React.Fragment>
            ))
          )}
          {/* The move actually played here: why this position is in the queue. */}
          {plies.length > 0 ? (
            revealed ? (
              <button
                type="button"
                className={`cm-repair-move cm-repair-move-mistake ${moveTypeClass(item.MoveType)}${
                  cursor === mistakeIndex ? ' is-current' : ''
                }`}
                onClick={() => onGoTo(mistakeIndex)}
                title={`You played ${item.PlayedMove}: ${MoveType[item.MoveType]}`}
              >
                <MistakeIcon className="cm-repair-move-icon" />
                {item.PlayedMove}
              </button>
            ) : (
              <button
                type="button"
                // Never marked current: it stands for the move still to be made; the ply before carries the
                // highlight.
                className="cm-repair-move cm-repair-move-todo"
                onClick={() => onGoTo(solveIndex)}
                title="Your move. This is the one to repair"
              >
                ?
              </button>
            )
          ) : null}
        </div>
        <p className="cm-repair-panel-note">
          {item.GameMoveTotal} moves in the game · this is move {item.Number}
        </p>
      </section>

      {/* Where this mistake sits on the repetition ladder: what "repaired 3×" bought you. */}
      <section className="cm-repair-panel-section cm-repair-ladder">
        <h6 className="cm-repair-panel-head">
          <FontAwesomeIcon icon={faRotateRight} className="me-2" />
          Your history here
        </h6>
        {item.RepairCount === 0 ? (
          <p className="cm-repair-panel-note">
            You’ve never repaired this one. Get it right and it comes back tomorrow.
          </p>
        ) : (
          <p className="cm-repair-panel-note">
            Repaired {item.RepairCount}×{item.Streak > 1 ? `, ${item.Streak} in a row` : ''}
            {relativeDays(item.LastRepairedAt) ? ` · last ${relativeDays(item.LastRepairedAt)}` : ''}
            {item.NextReviewAt && !item.IsDue
              ? ` · due back ${relativeDays(item.NextReviewAt)}`
              : ''}
          </p>
        )}
      </section>

      <RepairNote item={item} isAuthenticated={isAuthenticated} />
    </div>
  );
}

// The player's own note on this position, written mid-drill, when they know what to tell themselves.
// Private and keyed by the move's PGN, so it's waiting in the game's move panel afterwards.
function RepairNote(props: {
  item: RepairQueueItem;
  isAuthenticated: boolean;
}): React.ReactNode {
  const { item, isAuthenticated } = props;
  const [existing, setExisting] = useState<CommentModel | null>(null);
  const [body, setBody] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'saving' | 'saved' | 'failed'>('loading');

  // One cheap fetch per mistake: the game's thread narrowed to this move.
  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setExisting(null);
    setBody('');
    if (!isAuthenticated) {
      setState('ready');
      return;
    }
    CommentsAPI.getThread(item.GameId)
      .then((all) => {
        if (cancelled) return;
        const mine = all.find(
          (c) => c.IsMine && c.Status !== 'Removed' && c.MovePgn === item.MovePgn,
        );
        setExisting(mine ?? null);
        setBody(mine?.Body ?? '');
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [item.GameId, item.MovePgn, isAuthenticated]);

  const save = async () => {
    const text = body.trim();
    if (text === '' || item.MovePgn == null) return;
    setState('saving');
    try {
      const saved =
        existing != null
          ? await CommentsAPI.update(existing.Id, { Body: text, IsPrivate: true })
          : await CommentsAPI.create({
              GameId: item.GameId,
              MovePgn: item.MovePgn,
              ParentId: null,
              QuotedCommentId: null,
              Body: text,
              IsPrivate: true,
            });
      setExisting(saved);
      setState('saved');
    } catch {
      setState('failed');
    }
  };

  // A note needs a move PGN to hang off; without one, don't offer the box rather than drop the text.
  if (item.MovePgn == null) {
    return null;
  }

  return (
    <section className="cm-repair-panel-section cm-repair-note">
      <h6 className="cm-repair-panel-head">
        <FontAwesomeIcon icon={faNoteSticky} className="me-2" />
        Your note
      </h6>
      {!isAuthenticated ? (
        <p className="cm-repair-panel-empty">Sign in to keep a note on this position.</p>
      ) : state === 'loading' ? (
        <p className="cm-repair-panel-empty">Loading…</p>
      ) : (
        <>
          <textarea
            className="cm-repair-note-box"
            rows={3}
            value={body}
            placeholder="What will you tell yourself next time you see this?"
            onChange={(e) => {
              setBody(e.target.value);
              if (state === 'saved') setState('ready');
            }}
          />
          <div className="cm-repair-note-actions">
            <button
              type="button"
              className="cm-repair-note-save"
              onClick={save}
              disabled={state === 'saving' || body.trim() === ''}
            >
              {state === 'saving' ? 'Saving…' : existing != null ? 'Update note' : 'Save note'}
            </button>
            <span className="cm-repair-note-state text-muted">
              {state === 'saved'
                ? 'Saved. Only you can see it.'
                : state === 'failed'
                  ? 'Could not save that.'
                  : 'Private to you.'}
            </span>
          </div>
        </>
      )}
    </section>
  );
}
